/******************************************************************************
 * AdminFeatureFlagsTab: settings tab to enable/disable feature flags configured
 * on the core component.
 *
 * Flag keys are developer-owned (reconciled at startup): admins can only toggle
 * their on/off state, not create or delete them. Retired keys (enabled=false) are
 * hidden as they are no longer actionable. Endpoints (core, app module):
 *   GET  admin/feature-flags            -> list all flags
 *   PUT  admin/feature-flags/{id}/active -> { active } toggle
 *   POST admin/safety-diag/prefetch      -> pull + cache the latest diag_secu flow
 *****************************************************************************/

/* Imports */
import { useCallback, useEffect, useState } from "react";
import { t } from "i18next";
import toast from "react-hot-toast";
import { Button } from "@material-tailwind/react";
import { ArrowPathIcon } from "@heroicons/react/24/solid";
/***/

/* Services */
import Loading from "../../../../../../components/loading/Loading";
import { Table } from "../../../../../../components/table/Table";
import { api } from "../../../../../../services/api/api";
import { showApiErrorToast } from "../../../../../../services/api/error";
import FeatureFlagListItem from "../../../../../../components/data/feature-flag/FeatureFlagListItem";
/***/

/**
* AdminFeatureFlagsTab: admin feature flags tab
*/
export default function AdminFeatureFlagsTab() {
  const [flags, setFlags] = useState<EasyTech.FeatureFlag[] | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [prefetching, setPrefetching] = useState(false);

  const TABLE_HEAD = [
    { label: "Feature", orderKey: null },
    { label: "Code", orderKey: null },
    { label: "Enabled", orderKey: null }
  ];

  /**
  * loadFlags: fetch feature flags, keeping only non-retired (enabled) keys
  */
  const loadFlags = useCallback(() => {
    api.get("admin/feature-flags")
      .then((ret) => setFlags(((ret as EasyTech.FeatureFlag[]) || []).filter((f) => f.enabled)))
      .catch((err) => {
        console.error(err);
        setFlags([]);
      });
  }, []);

  /**
  * toggleFlag: turn a feature flag on/off
  * @param flag - the flag to toggle
  * @param active - the new active state
  */
  const toggleFlag = (flag: EasyTech.FeatureFlag, active: boolean) => {
    setTogglingId(flag.id);
    api.put("admin/feature-flags/" + flag.id + "/active", { active })
      .then((ret) => {
        const updated = ret as EasyTech.FeatureFlag;
        setFlags((prev) => (prev || []).map((f) => (f.id === flag.id ? { ...f, active: updated.active } : f)));
        toast.success(active ? "Feature enabled" : "Feature disabled");
      })
      .catch((error) => {
        console.error(error);
        showApiErrorToast(error, { fallbackMessage: "Error while updating the feature flag" });
        // Reload to reflect the true server state (e.g. a rejected activation).
        loadFlags();
      })
      .finally(() => setTogglingId(null));
  };
  /***/

  /**
  * prefetchDiagSecu: pull + cache the latest diag_secu flow snapshot on demand. Useful to refresh
  * the cache after a new flow is published upstream, without toggling the DIAG_SECU flag off/on.
  */
  const prefetchDiagSecu = () => {
    setPrefetching(true);
    api.post("admin/safety-diag/prefetch", {})
      .then((ret) => {
        const snap = ret as { versionNumber?: number };
        toast.success(t("settings:safetyDiag.prefetch.success", { version: snap?.versionNumber ?? "" }));
      })
      .catch((error) => {
        console.error(error);
        showApiErrorToast(error, { fallbackMessage: t("settings:safetyDiag.prefetch.error") });
      })
      .finally(() => setPrefetching(false));
  };
  /***/

  useEffect(() => {
    loadFlags();
  }, [loadFlags]);

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button
          size="sm"
          variant="outlined"
          onClick={prefetchDiagSecu}
          disabled={prefetching}
          data-testid="prefetch-diag-secu"
          className="flex items-center gap-2">
          <ArrowPathIcon className={`h-4 w-4 ${prefetching ? "animate-spin" : ""}`} />
          {t("settings:safetyDiag.prefetch.button")}
        </Button>
      </div>

      <div className="my-6">
        {flags ?
          <Table tableHead={TABLE_HEAD}
            emptyTable={(!flags || flags.length === 0) ? "No feature flags" : false}>
            {flags.map((flag, index) => (
              <FeatureFlagListItem
                key={flag.id}
                flag={flag}
                disabled={togglingId === flag.id}
                isLast={index === flags.length - 1}
                onToggle={(active) => toggleFlag(flag, active)} />
            ))}
          </Table>
          : <Loading />}
      </div>
    </div>
  );
}
/***/
